// React
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { flushSync } from 'react-dom';

// Third-party libraries
import { Dropdown, Popover, Space, Tooltip } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { head, pathOr, propOr } from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router';
import { arrayMove } from '@dnd-kit/sortable';
import { toast } from 'react-toastify';

// Local imports
import { CalenderIcon, Star, VerticalDots } from '../../assets';
import { BASE_URL } from '../../constants/api-urls';
import { ITEMS_PER_PAGE, ENABLE_VIRTUALIZATION } from '../../constants/common';
import { useLoginContext } from '../../context';
import { usePlaylistContext } from '../../context/PlaylistContext';
import { AddToPlayListModal, LoadingSpinner } from '../../components';
import PlaylistNewBanner from './PlaylistNewBanner';
import EditAddToPlayListModal from '../../components/AddToPlayListModal/EditAddToPlayListModal';
import DeleteModal from '../../components/Subscriptions/CancelSubscription';
import ShareModal from '../../components/ShareModal/ShareModal';
import InstantShareModal from '../../components/ShareModal/InstantShareModal';
import DeleteAndFixModal from '../../components/DeleteAndFixModal/DeleteAndFixModal';
import SongsTableNew from '../../components/SongsTable/SongsTableNew';
import VirtualizedSongsTable from '../../components/SongsTable/VirtualizedSongsTable';
import PlaylistSongDropdown from './PlaylistSongDropdown';
import { PlaylistNewStyle, TablesStyle } from './PlaylistNew.style';
import { KeyCode, deepEqual, truncateText } from '../../utils';
import { resetPlaylistPositions } from '../../utils/playlistReset';
import { resolveOriginalDateFromRefreshRecord } from '../../utils/playlistRefreshRecord';
import { getAllTracks, deleteAllTracks, addAllTracks } from '../../utils';
import SpotifyApiService from '../../services/SpotifyApiService';

interface PlaylistData {
  snapshot_id: string;
  id: string;
  tracks?: {
    total: number;
  };
  name?: string;
  description?: string;
  images?: Array<{ url: string }>;
}

interface SongItem {
  item: {
    id: string;
    uri: string;
    name: string;
    preview_url: string;
    popularity: number;
    artists: Array<{ name: string }>;
    album: {
      images: Array<{ url: string }>;
    };
    external_urls: {
      spotify: string;
    };
  };
  track?: {
    id: string;
    uri: string;
    name: string;
    preview_url: string;
    popularity: number;
    artists: Array<{ name: string }>;
    album: {
      images: Array<{ url: string }>;
    };
    external_urls: {
      spotify: string;
    };
  };
  added_at: string;
  position?: number;
  index?: number;
  _id?: string;
}

const PlaylistNew = () => {
  const { playlistId } = useParams<{ playlistId: string }>();
  const { state: loginState } = useLoginContext();
  const { state: playlistState, dispatch: playlistDispatch } = usePlaylistContext();

  // Core playlist data
  const [currentPlaylistData, setCurrentPlaylistData] = useState<PlaylistData>({
    snapshot_id: '',
    id: '',
  });
  const [songs, setSongs] = useState<SongItem[]>([]);
  const [processedSongs, setProcessedSongs] = useState<any[]>([]);
  const [scheduleData, setScheduleData] = useState<any[]>([]);
  const [playlistRefreshRecord, setPlaylistRefreshRecord] = useState<any[]>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isReloading] = useState(false);

  // Pagination
  const [hasMoreData, setHasMoreData] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);

  // Selection states
  const [selectedSongs, setSelectedSongs] = useState<any[]>([]);
  const [selectedSongsIds, setSelectedSongsIds] = useState<any[]>([]);
  const [bulkAction, setBulkAction] = useState(false);

  // Modal states
  const [modals, setModals] = useState({
    addToPlaylist: false,
    editPlaylist: false,
    shareModal: false,
    instantShare: false,
    deleteModal: false,
    deleteSelected: false,
    deleteFix: false,
    refresh: false,
  });

  // Other states
  const [viewDetailData, setViewDetailData] = useState<any>('');
  const [editType, setEditType] = useState('');
  const [fixDataParams, setFixDataParams] = useState<any>(null);
  const [loadingText, setLoadingText] = useState('');
  const [loadingTextHead, setLoadingTextHead] = useState('');
  const [refreshScheduleData, setRefreshScheduleData] = useState<any>(null);
  const [fixScheduleData, setFixScheduleData] = useState<any>(false);

  // Refs for scroll position and performance
  const playlistRef = useRef<HTMLElement>(null);
  const scrollPositionRef = useRef(0);
  const isLoadingRef = useRef(false);
  const isReloadingRef = useRef(false);

  // Table refs (moved to top level to avoid hook order issues)
  const numberRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const popularityRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);
  const spotifyRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLDivElement>(null);

  // Memoized total songs count
  const totalSongs = useMemo(
    () => pathOr(0, ['tracks', 'total'], currentPlaylistData),
    [currentPlaylistData]
  );

  // Note: Removed useAxios hook - now using SpotifyApiService for token management

  // Create lookup map for schedule data (performance optimization)
  const scheduleDataMap = useMemo(() => {
    const map: Record<string, any> = {};
    if (Array.isArray(scheduleData)) {
      scheduleData.forEach(item => {
        if (item?.song_uri) {
          map[item.song_uri] = item;
        }
      });
    }
    return map;
  }, [scheduleData]);

  // Save scroll position before loading
  const saveScrollPosition = useCallback(() => {
    if (playlistRef.current) {
      scrollPositionRef.current = playlistRef.current.scrollTop;
    }
  }, []);

  // Restore scroll position after loading
  const restoreScrollPosition = useCallback(() => {
    if (playlistRef.current && scrollPositionRef.current > 0) {
      setTimeout(() => {
        if (playlistRef.current) {
          playlistRef.current.scrollTop = scrollPositionRef.current;
        }
      }, 100);
    }
  }, []);

  // Modal helper functions
  const updateModal = useCallback((modalName: keyof typeof modals, value: boolean) => {
    setModals(prev => ({ ...prev, [modalName]: value }));
  }, []);

  // Handle song click for preview
  const handleSongClick = useCallback(
    (track: any) => {
      if (track.preview_url) {
        playlistDispatch({
          type: 'SET_PREVIEW_SONG',
          payload: {
            url: track.preview_url,
            id: track.id,
            name: track.name,
            artist: track.artists[0].name,
            image: track.album.images[0]?.url,
            external: track.external_urls.spotify,
          },
        });
      } else {
        window.open(track.external_urls.spotify, '_blank');
      }
    },
    [playlistDispatch]
  );

  // Update song end date optimistically
  const updateSongEndDate = useCallback((songUri: string, newEndDate: string, songId: string) => {
    // Use flushSync to force immediate, synchronous UI update
    flushSync(() => {
      setScheduleData(prev => {
        const songExists = prev.some(item => item.song_uri === songUri);

        if (songExists) {
          // Update existing song
          return prev.map(item =>
            item.song_uri === songUri ? { ...item, end_time: newEndDate } : item
          );
        } else if (newEndDate && newEndDate !== 'null') {
          // Add new song to scheduleData if it doesn't exist and we're setting an end date
          return [
            ...prev,
            {
              song_uri: songUri,
              end_time: newEndDate,
              _id: songId !== 'nothing' ? songId : undefined,
            },
          ];
        }

        return prev;
      });
    });

    // The flushSync ensures the state update and all resulting re-renders
    // (including the useEffect that watches scheduleData) complete synchronously
  }, []);

  // Update song lock position optimistically
  const updateSongLockPosition = useCallback(
    (songUri: string, isLocked: boolean, position: number, playId: string) => {
      // Use flushSync to force immediate, synchronous UI update
      flushSync(() => {
        setScheduleData(prev => {
          const songExists = prev.some(item => item.song_uri === songUri);

          if (songExists) {
            // Update existing song
            return prev.map(item =>
              item.song_uri === songUri
                ? { ...item, lockPosition: isLocked, ...(isLocked ? { position } : {}) }
                : item
            );
          } else if (isLocked) {
            // Add new song to scheduleData if it doesn't exist and we're locking it
            return [
              ...prev,
              {
                song_uri: songUri,
                lockPosition: isLocked,
                position: position,
                _id: playId !== 'nothing' ? playId : undefined,
              },
            ];
          }

          return prev;
        });
      });

      // The flushSync ensures the state update and all resulting re-renders
      // (including the useEffect that watches scheduleData) complete synchronously
    },
    []
  );

  // Update song notes optimistically
  const updateSongNotes = useCallback((songUri: string, newNotes: string, songId: string) => {
    // Use flushSync to force immediate, synchronous UI update
    flushSync(() => {
      setScheduleData(prev => {
        const songExists = prev.some(item => item.song_uri === songUri);

        if (songExists) {
          // Update existing song
          return prev.map(item =>
            item.song_uri === songUri ? { ...item, notes: newNotes } : item
          );
        } else if (newNotes) {
          // Add new song to scheduleData if it doesn't exist and we're adding notes
          return [
            ...prev,
            {
              song_uri: songUri,
              notes: newNotes,
              _id: songId !== 'nothing' ? songId : undefined,
            },
          ];
        }

        return prev;
      });
    });

    // The flushSync ensures the state update and all resulting re-renders
    // (including the useEffect that watches scheduleData) complete synchronously
  }, []);

  // Update song position optimistically (for move song modal)
  const updateSongPosition = useCallback(
    (
      songUri: string,
      oldPlaylistId: string,
      newPlaylistId: string | string[] | null,
      newPosition: number | null
    ) => {
      // Use flushSync to force immediate, synchronous UI update
      flushSync(() => {
        // Case 1: Song was removed from current playlist (newPlaylistId is null or doesn't include current)
        if (
          newPlaylistId === null ||
          (typeof newPlaylistId === 'string' && newPlaylistId !== oldPlaylistId) ||
          (Array.isArray(newPlaylistId) && !newPlaylistId.includes(oldPlaylistId))
        ) {
          // Remove the song from both arrays
          setSongs(prev => prev.filter(song => song.item?.uri !== songUri));
          setProcessedSongs(prev => prev.filter(song => song.item?.uri !== songUri));
          return;
        }

        // Case 2: Song position changed within current playlist or was copied to other playlists
        // (current playlist is still included)
        const currentIndex = songs.findIndex(song => song.item?.uri === songUri);

        if (currentIndex === -1 || newPosition === null) {
          return;
        }

        // Calculate new index (position is 1-based, index is 0-based)
        const targetIndex = newPosition - 1;

        // Only update if position actually changed
        if (currentIndex !== targetIndex) {
          // Update both songs and processedSongs arrays
          setSongs(prev => arrayMove(prev, currentIndex, targetIndex));
          setProcessedSongs(prev => arrayMove(prev, currentIndex, targetIndex));
        }
      });

      // The flushSync ensures the state update and all resulting re-renders complete synchronously
    },
    [songs]
  );

  // Handle songs reorder after successful drag operation
  // Supports both single-song and multi-song moves
  // For multi-song: oldIndex is startIndex, newIndex is dropTargetIndex, count is number of items
  const handleSongsReorder = useCallback((oldIndex: number, newIndex: number, count: number = 1) => {
    const innerFunc = () => {
      if (count === 1) {
        // Single song move - use existing arrayMove logic
        setSongs(prev => arrayMove(prev, oldIndex, newIndex));
      } else {
        // Multi-song move - move a range of items
        setSongs(prev => {
          const itemsToMove = prev.slice(oldIndex, oldIndex + count);
          const stateWithoutItems = [
            ...prev.slice(0, oldIndex),
            ...prev.slice(oldIndex + count)
          ];
          
          // Calculate insertion point after items are removed
          // When dragging forward: insert AFTER the target, adjusted for removed items
          // When dragging backward: insert AT the target position
          let insertAt: number;
          if (newIndex > oldIndex) {
            // Dragging forward: (newIndex + 1) - count
            insertAt = newIndex - count + 1;
          } else {
            // Dragging backward: insert at the target position
            insertAt = newIndex;
          }
          
          return [
            ...stateWithoutItems.slice(0, insertAt),
            ...itemsToMove,
            ...stateWithoutItems.slice(insertAt)
          ];
        });
      }
    };
    innerFunc();
  }, []);

  // Handle song deletion
  const handleItemDelete = useCallback(
    async (row: any) => {
      setEditType('refresh');
      setLoadingText('Deleting the song');
      setLoadingTextHead('Delete song');
      updateModal('editPlaylist', true);

      // Save scroll position before state change
      saveScrollPosition();

      try {
        const deletePayload = {
          items: [
            {
              uri: row.item.uri,
            },
          ],
        };

        // Get Auth0 user ID and profile info from localStorage
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
        const auth0UserId = userInfo?.userId || loginState.userId || '';

        // If on shared profile, use owner's profile ObjectId, otherwise use accessor's Auth0 ID
        const profileIdentifier =
          spotifyUser.isShared === true && spotifyUser.profileId
            ? spotifyUser.profileId // Owner's profile ObjectId for shared profiles
            : auth0UserId; // Accessor's Auth0 ID for own profiles

        // API calls in parallel
        await Promise.all([
          axios.delete(`https://api.spotify.com/v1/playlists/${playlistId}/items`, {
            headers: { Authorization: `Bearer ${loginState.token}` },
            data: JSON.stringify(deletePayload),
          }),
          row._id
            ? axios.post(`${BASE_URL}/DeleteSongfromPlaylist/delete`, {
                song_uri: row.item.uri,
                selected_playlist_Id: playlistId,
                _id: row._id,
                userId: auth0UserId, // Accessor's Auth0 ID for access validation
                profileId: profileIdentifier, // Profile identifier (ObjectId for shared, Auth0 ID for own)
              })
            : Promise.resolve(),
        ]);

        // SUCCESS: Update local state instead of reload
        const songUri = row.item.uri;

        // Remove from songs array
        setSongs(prev => prev.filter(song => song.item.uri !== songUri));

        // Remove from processedSongs
        setProcessedSongs(prev => prev.filter(song => song.rowNumber?.e?.item?.uri !== songUri));

        // Remove from scheduleData if exists
        setScheduleData(prev => prev.filter(item => item.song_uri !== songUri));

        // Update total count
        setCurrentPlaylistData(prev => ({
          ...prev,
          tracks: {
            ...prev.tracks,
            total: prev.tracks.total - 1,
          },
        }));

        toast.success('Song deleted successfully');

        // Restore scroll position
        setTimeout(() => {
          restoreScrollPosition();
        }, 100);
      } catch (error) {
        console.error('Error deleting song:', error);
        toast.error('Failed to delete song');
      } finally {
        updateModal('editPlaylist', false);
        setLoadingText('');
        setLoadingTextHead('');
      }
    },
    [
      playlistId,
      loginState.token,
      loginState.userId,
      updateModal,
      saveScrollPosition,
      restoreScrollPosition,
    ]
  );

  // Modal handlers
  const showModalDeleteFix = useCallback(
    (row: any, callback: any) => {
      updateModal('deleteFix', true);
      setFixDataParams({ row, callback });
    },
    [updateModal]
  );

  // Scroll position restoration - safer implementation to avoid re-rendering loops
  const scrollRestoreRef = useRef<(() => void) | null>(null);

  const handleModalCloseRestoreScroll = useCallback(() => {
    // Call the scroll restoration function provided by SongsTableNew
    if (scrollRestoreRef.current) {
      scrollRestoreRef.current();
    }
  }, []);

  // Callback to receive scroll restoration function from SongsTableNew
  const onScrollRestore = useCallback((restoreFn: () => void) => {
    // Use ref instead of state to avoid re-renders
    scrollRestoreRef.current = restoreFn;
  }, []);

  // Process songs data efficiently
  const processSongData = useCallback(
    (songItems: SongItem[], isLoadMore = false) => {
      const processedItems = songItems.map((item, index) => {
        const actualIndex = isLoadMore ? currentOffset + index : index;
        const scheduleInfo = scheduleDataMap[item.item?.uri];
        const originalDate = resolveOriginalDateFromRefreshRecord(
          playlistRefreshRecord,
          item?.item?.uri,
          item?.added_at
        );

        // Extract filtering properties
        const endTime = scheduleInfo?.end_time;
        const notes = scheduleInfo?.notes;
        const lockPosition = scheduleInfo?.lockPosition;

        const baseData = {
          key: false,
          uniqueId: uuidv4(),
          _id: scheduleInfo?._id,
          id: item.item?.id,
          rowNumber: {
            indexes: actualIndex + 1,
            e: item,
            songUri: item?.item?.uri,
          },
          popularity: item?.item?.popularity ?? '--',
          currentSongIndex: actualIndex,
          position: actualIndex,
          lockPosition: lockPosition || false,
          lockPositionNumber: scheduleInfo?.position,
          previewUrl: item?.item?.preview_url,
          externalUrl: item?.item?.external_urls.spotify,
          endTime,
          notes,
        };

        // Store raw filtering data for SongPlace Only
        const songData = {
          ...baseData,
          _rawEndTime: endTime,
          _rawNotes: notes,
          _rawLockPosition: lockPosition,
        };

        // Add UI components
        return {
          ...songData,
          image: (
            <span
              id="hancker"
              onClick={() => handleSongClick(item.item)}
              style={{ cursor: 'pointer' }}
            >
              <img
                className="table-list-image"
                alt="thumbnail"
                src={item?.item?.album?.images[0]?.url}
                width="40"
                height="40"
              />
            </span>
          ),
          name: (
            <>
              <Popover content={<p>{item?.item?.name}</p>} placement="topLeft">
                <p
                  id="hancker"
                  onClick={() => handleSongClick(item.item)}
                  style={{ cursor: 'pointer' }}
                >
                  {truncateText(item?.item?.name, 23)}
                </p>
              </Popover>
              <p
                id="hancker"
                onClick={() => handleSongClick(item.item)}
                style={{ cursor: 'pointer' }}
              >
                {truncateText(item?.item?.artists[0]?.name, 18)}
              </p>
            </>
          ),
          startTime: (
            <Popover
              overlayClassName="newStylePopup"
              content={
                <p className="hanker2">{moment(originalDate).format('DD/MM/YYYY hh:mm A')}</p>
              }
              placement="topLeft"
              title="Original date"
            >
              <p id="hancker1">{moment(item.added_at).format('DD/MM/YYYY hh:mm A')}</p>
            </Popover>
          ),
          endTime:
            endTime && endTime !== 'null' ? (
              <Popover
                content={<p className="hanker2">{moment(endTime).format('DD/MM/YYYY hh:mm A')}</p>}
                placement="topLeft"
              >
                <p id="hancker1">{moment(endTime).format('DD/MM/YYYY hh:mm A')}</p>
              </Popover>
            ) : null,
          notes: notes || '',
          Edit: (editobj: any) => (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: '0px 20px',
              }}
            >
              {lockPosition && (
                <Tooltip title={`Position locked at ${Number(scheduleInfo?.position) + 1}`}>
                  <span className="starPosition">
                    <Star />
                  </span>
                </Tooltip>
              )}
              {notes && (
                <Popover content={<p>{notes}</p>} placement="topLeft">
                  <div style={{ cursor: 'pointer' }}>
                    <CalenderIcon />
                  </div>
                </Popover>
              )}
              <TablesStyle>
                <div className="clay" style={{ display: 'flex' }}>
                  <Dropdown
                    overlay={() => (
                      <PlaylistSongDropdown
                        data={{
                          ...editobj,
                          ...item,
                          ...scheduleInfo,
                          songUri: item?.item?.uri,
                          playlist: playlistId,
                          position: actualIndex,
                          playId: scheduleInfo?._id || 'nothing',
                          edit: !!scheduleInfo,
                          index: actualIndex,
                          indexes: actualIndex, // Add indexes field for editSongToPlaylist
                          endTime,
                        }}
                        loginState={loginState}
                        scheduleData={scheduleData}
                        playlistIdRef={{ current: playlistId }}
                        setEditType={setEditType}
                        setEditAddToPlaylistModal={value => updateModal('editPlaylist', value)}
                        setViewDetailData={setViewDetailData}
                        showModalDeleteFix={showModalDeleteFix}
                        setShareSongModal={value => updateModal('shareModal', value)}
                        handleItemDelete={handleItemDelete}
                        onLockPositionSuccess={updateSongLockPosition}
                      />
                    )}
                    placement={index > songItems.length - 5 ? 'topLeft' : 'bottomLeft'}
                  >
                    <span onClick={e => e.preventDefault()} style={{ cursor: 'pointer' }}>
                      <Space>
                        <VerticalDots />
                      </Space>
                    </span>
                  </Dropdown>
                </div>
              </TablesStyle>
            </div>
          ),
          clickHandleFn: () => handleSongClick(item?.item),
        };
      });

      // Apply SongPlace Only filtering if enabled
      if (playlistState?.isSongPlace === true) {
        const filteredItems = processedItems.filter(item => {
          // Show only songs that have: endTime, notes, or lockPosition
          const hasEndTime = item._rawEndTime && item._rawEndTime !== 'null';
          const hasNotes = item._rawNotes;
          const hasLockPosition = item._rawLockPosition;
          const shouldShow = hasEndTime || hasNotes || hasLockPosition;

          if (shouldShow) {
          }

          return shouldShow;
        });
        return filteredItems;
      }

      return processedItems;
    },
    [
      scheduleDataMap,
      currentOffset,
      playlistId,
      loginState,
      scheduleData,
      handleSongClick,
      updateModal,
      showModalDeleteFix,
      handleItemDelete,
      playlistState?.isSongPlace,
      playlistRefreshRecord,
      updateSongLockPosition,
    ]
  );

  // Load initial playlist data
  const loadInitialData = useCallback(async () => {
    if (!playlistId || !loginState.token || isLoadingRef.current) return;

    setIsLoading(true);
    isLoadingRef.current = true;

    try {
      // 🔥 ALL API CALLS RUN IN PARALLEL
      const [playlistResponse, scheduleResponse, refreshResponse, songsResponse] =
        await Promise.all([
          // 1. Spotify playlist info - Using SpotifyApiService for token management
          SpotifyApiService.getInstance().getPlaylist(playlistId),

          // 2. Schedule data
          axios.get(`${BASE_URL}/getrequestsbysonguri/${loginState.userId}/${playlistId}`),

          // 3. Playlist refresh record
          axios.get(`${BASE_URL}/PlaylistRefreshRecord/${playlistId}/data`, {
            headers: { 'Content-Type': 'application/json' },
          }),

          // 4. Initial songs - Using SpotifyApiService for token management
          SpotifyApiService.getInstance().getPlaylistTracks(playlistId, ITEMS_PER_PAGE, 0),
        ]);

      // 🎯 STATE UPDATES
      setCurrentPlaylistData(playlistResponse.data);

      setScheduleData(scheduleResponse.data?.data || []);

      setPlaylistRefreshRecord(refreshResponse.data?.data || []);

      if (songsResponse.data?.items) {
        setSongs(songsResponse.data.items);
        setCurrentOffset(ITEMS_PER_PAGE);
        setHasMoreData(songsResponse.data.items.length === ITEMS_PER_PAGE);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setIsLoading(false);
      setInitialLoad(false);
      isLoadingRef.current = false;
    }
  }, [playlistId, loginState.token, loginState.userId]);

  // Load more songs
  const loadMoreSongs = useCallback(async () => {
    if (!hasMoreData || isLoadingRef.current || isLoadingMore) return;

    setIsLoadingMore(true);
    isLoadingRef.current = true;
    saveScrollPosition();

    try {
      const response = await SpotifyApiService.getInstance().getPlaylistTracks(
        playlistId,
        ITEMS_PER_PAGE,
        currentOffset
      );

      if (response.data?.items) {
        const newSongs = response.data.items;
        setSongs(prev => [...prev, ...newSongs]);
        setCurrentOffset(prev => prev + ITEMS_PER_PAGE);
        setHasMoreData(newSongs.length === ITEMS_PER_PAGE);

        // Restore scroll position after a short delay
        setTimeout(() => {
          restoreScrollPosition();
        }, 50);
      }
    } catch (error) {
      console.error('Error loading more songs:', error);
      toast.error('Failed to load more songs');
    } finally {
      setIsLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, [
    hasMoreData,
    isLoadingMore,
    currentOffset,
    playlistId,
    saveScrollPosition,
    restoreScrollPosition,
  ]);

  // Process songs when songs, schedule data, or SongPlace mode changes
  useEffect(() => {
    if (!initialLoad) {
      const processed = processSongData(songs);
      setProcessedSongs(processed);
    }
  }, [songs, processSongData, initialLoad]);

  // Reprocess songs when SongPlace Only mode changes
  useEffect(() => {
    if (!initialLoad) {
      const processed = processSongData(songs);
      setProcessedSongs(processed);
    }
  }, [playlistState?.isSongPlace, songs, processSongData, initialLoad]);

  // Track the last loaded playlist to prevent double calls
  const lastLoadedPlaylistRef = useRef<string>('');

  // Combined: Reset data and initialize when playlist or auth changes
  useEffect(() => {
    if (playlistId) {
      // Always reset state when playlist changes (even if same playlist but auth wasn't ready before)
      if (playlistId !== lastLoadedPlaylistRef.current) {
        // STEP 1: Reset state first
        isLoadingRef.current = false;
        isReloadingRef.current = false;

        setCurrentPlaylistData({
          snapshot_id: '',
          id: '',
        });

        playlistDispatch({
          type: 'SET_SELECTED_PLAYLIST_ID',
          payload: playlistId,
        });

        setSongs([]);
        setProcessedSongs([]);
        setScheduleData([]);
        setPlaylistRefreshRecord([]);
        setCurrentOffset(0);
        setHasMoreData(true);
        setInitialLoad(true);
        setIsLoading(true);
        setSelectedSongs([]);
        setSelectedSongsIds([]);
        setBulkAction(false);

        if (playlistRef.current) {
          playlistRef.current.scrollTop = 0;
        }
      }

      // STEP 2: Load data if auth is available and we haven't loaded this playlist yet
      if (
        loginState.token &&
        loginState.userId &&
        playlistId !== lastLoadedPlaylistRef.current &&
        !isLoadingRef.current
      ) {
        lastLoadedPlaylistRef.current = playlistId;
        loadInitialData();
      }
    }
  }, [playlistId, loginState.token, loginState.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle bulk deletion
  const handleBulkDelete = useCallback(async () => {
    setEditType('refresh');
    setLoadingText('Deleting songs');
    setLoadingTextHead('Delete songs');
    updateModal('editPlaylist', true);

    // Save scroll position before state change
    saveScrollPosition();

    try {
      // Get Auth0 user ID and profile info from localStorage
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
      const auth0UserId = userInfo?.userId || loginState.userId || '';

      // If on shared profile, use owner's profile ObjectId, otherwise use accessor's Auth0 ID
      const profileIdentifier =
        spotifyUser.isShared === true && spotifyUser.profileId
          ? spotifyUser.profileId // Owner's profile ObjectId for shared profiles
          : auth0UserId; // Accessor's Auth0 ID for own profiles

      // API calls in parallel for better performance
      await Promise.all([
        axios.delete(`https://api.spotify.com/v1/playlists/${playlistId}/items`, {
          headers: { Authorization: `Bearer ${loginState.token}` },
          data: JSON.stringify({ items: selectedSongs }),
        }),
        selectedSongsIds.length > 0
          ? axios.post(`${BASE_URL}/deleteMultipleSongs`, {
              ids: selectedSongsIds,
              userId: auth0UserId, // Accessor's Auth0 ID for access validation
              profileId: profileIdentifier, // Profile identifier (ObjectId for shared, Auth0 ID for own)
            })
          : Promise.resolve(),
      ]);

      // SUCCESS: Update local state instead of reload
      const deletedUris = selectedSongs.map(song => song.uri);

      // Remove from songs array
      setSongs(prev => prev.filter(song => !deletedUris.includes(song.item.uri)));

      // Remove from processedSongs
      setProcessedSongs(prev =>
        prev.filter(song => !deletedUris.includes(song.rowNumber?.e?.item?.uri))
      );

      // Remove from scheduleData if exists
      setScheduleData(prev => prev.filter(item => !deletedUris.includes(item.song_uri)));

      // Clear selections
      setSelectedSongs([]);
      setSelectedSongsIds([]);
      setBulkAction(false);

      // Update total count
      setCurrentPlaylistData(prev => ({
        ...prev,
        tracks: {
          ...prev.tracks,
          total: prev.tracks.total - selectedSongs.length,
        },
      }));

      toast.success(`${selectedSongs.length} songs deleted successfully`);

      // Restore scroll position
      setTimeout(() => {
        restoreScrollPosition();
      }, 100);
    } catch (error) {
      console.error('Error deleting songs:', error);
      toast.error('Failed to delete songs');
    } finally {
      updateModal('editPlaylist', false);
      setLoadingText('');
      setLoadingTextHead('');
    }
  }, [
    playlistId,
    loginState.token,
    loginState.userId,
    selectedSongs,
    selectedSongsIds,
    updateModal,
    saveScrollPosition,
    restoreScrollPosition,
  ]);

  // Note: handleItemDeleteFix removed - position conflicts now use page reload for state synchronization

  // Handle reset positions
  const handleResetPositions = useCallback(
    async (setSyncModal: (value: boolean) => void) => {
      try {
        setSyncModal(true);

        const result = await resetPlaylistPositions({
          playlistId: playlistId!,
          userId: loginState.userId,
          accessToken: loginState.token,
        });

        if (result.success) {
          if (result.data.positionsUpdated === 0) {
            toast.success('No need to reset — all songs are already in their correct positions.');
          } else {
            toast.success(
              `Successfully reset ${result.data.positionsUpdated} song positions out of ${result.data.songsProcessed} total songs`
            );
          }
          if (!isReloadingRef.current) {
            isReloadingRef.current = true;
            setTimeout(() => window.location.reload(), 1000);
          }
        } else {
          toast.error('Failed to reset positions');
        }
      } catch (error) {
        console.error('Reset operation failed:', error);
        toast.error(`Error: ${error.message}`);
      } finally {
        setSyncModal(false);
      }
    },
    [playlistId, loginState.userId, loginState.token]
  );

  // Handle refresh functionality
  const handleRefresh = useCallback(
    async (
      values: any,
      deleteAllRefresh: any,
      refreshPlaylist: any,
      token: string,
      songs: any,
      saveRestore: any
    ) => {
      updateModal('refresh', false);

      if (values?.frequency === 'spotifyPlaylistRefreshOperationOnce') {
        // Show loading modal
        setLoadingText('Refreshing playlist...');
        setLoadingTextHead('Instant Refresh');
        setEditType('refresh');
        updateModal('editPlaylist', true);

        try {
          // Call backend endpoint instead of doing refresh in frontend
          const response = await axios.post(`${BASE_URL}/instantRefreshPlaylist`, {
            playlist_id: playlistId,
            accessToken: loginState.refreshToken,
            backup: saveRestore,
          });

          if (response.data.success) {
            toast.success('Playlist refreshed successfully');
            // Reload the page after successful refresh
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            toast.error('Failed to refresh playlist');
            updateModal('editPlaylist', false);
            setEditType('');
          }
        } catch (error) {
          console.error('Error refreshing playlist:', error);
          toast.error('Failed to refresh playlist');
          updateModal('editPlaylist', false);
          setEditType('');
        }
      } else if (values.frequency === 'deleteRefresh') {
        // Note: deleteAllRefresh is passed as parameter but not imported/used directly here
        // This maintains backward compatibility with PlayListBanner component
        deleteAllRefresh(values.playlistId);
        window.location.reload();
      }
    },
    [playlistId, loginState.refreshToken, updateModal]
  );

  // Handle restore playlist songs
  const restorePlaylsitSongs = useCallback(
    async (
      restoreModal: any,
      setrestoreModal: any,
      versionToRestore: number,
      lastrefreshrecord: string
    ) => {
      try {
        const response = await axios.get(`${BASE_URL}/PlaylistRefreshRecord/${playlistId}/data`, {
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.data.data.length) {
          let allVersions = JSON.parse(response.data.data[0].uris);
          const selectedVersion = allVersions.find(
            (obj: any) => Number(obj.version) === Number(versionToRestore)
          );
          const songUris: string[] = [];

          selectedVersion?.uris?.forEach((song: any) => {
            if (typeof song?.uri === 'string' && song.uri.includes('spotify:track:')) {
              songUris.push(song.uri);
            }
          });

          if (!songUris.length) {
            toast.error('Selected backup does not contain valid songs to restore');
            setrestoreModal(false);
            return;
          }

          // Get all tracks and delete them using the reliable utility function
          const allSongs = await getAllTracks(loginState.token, playlistId);
          if (allSongs.length) {
            let uriWithoutlocal = allSongs.filter((song: any) =>
              song?.item?.uri.includes('spotify:track:')
            );
            const songUrischeck = uriWithoutlocal.map((song: any) => song.item.uri);
            const songsToBeDeleted = songUrischeck.map((song: any) => `{"uri":"${song}"}`);
            if (songsToBeDeleted.length) {
              await deleteAllTracks(loginState.token, playlistId, songsToBeDeleted);
            }
          }

          // Add songs from selected version using the reliable utility function
          await addAllTracks(loginState.token, playlistId, songUris);

          if (lastrefreshrecord) {
            await axios.put(`${BASE_URL}/updateRefreshlogs/${lastrefreshrecord}`, {
              errorInQueue: false,
            });
          }

          if (!isReloadingRef.current) {
            isReloadingRef.current = true;
            setTimeout(() => window.location.reload(), 1000);
          }
        } else {
          setrestoreModal(false);
        }
      } catch (error) {
        console.error('Error restoring playlist:', error);
        toast.error('Failed to restore playlist');
        setrestoreModal(false);
      }
    },
    [playlistId, loginState.token]
  );

  // Handle instant share initialization
  useEffect(() => {
    let instantShareDetail = JSON.parse(localStorage.getItem('instantShareDetail') || 'null');
    if (instantShareDetail) {
      updateModal('instantShare', true);
    }
  }, [updateModal]);

  // Song selection functions
  const selectSongFunction = useCallback((obj: any, id: any, index: number) => {
    setSelectedSongs(prev => {
      const exists = prev.some(item => deepEqual(item, obj));
      if (exists) {
        return prev.filter(item => !deepEqual(item, obj));
      } else {
        return [...prev, obj];
      }
    });

    setSelectedSongsIds(prev => {
      const exists = prev.some(item => deepEqual(item, obj));
      if (exists) {
        return prev.filter(item => !deepEqual(item, obj));
      } else {
        return id ? [...prev, id] : prev;
      }
    });
  }, []);

  // Get additional data functions
  const getScheduleRefreshData = useCallback(async (id: string) => {
    try {
      const response = await axios.get(`${BASE_URL}/getRefreshlogs/${id}`);
      if (response?.data?.data?.length) {
        setRefreshScheduleData(response.data.data[0]);
      } else {
        setRefreshScheduleData(null);
      }
    } catch (error) {
      console.error('Error fetching refresh data:', error);
    }
  }, []);

  const updateScheduleRefreshData = useCallback((newScheduleData: any) => {
    setRefreshScheduleData(newScheduleData);
  }, []);

  const clearScheduleRefreshData = useCallback(() => {
    setRefreshScheduleData(null);
  }, []);

  const updateScheduleResetScheduleData = useCallback((scheduleData: any) => {
    // Update fix schedule data without clearing lock positions
    // This is called when creating/updating a schedule reset schedule
    setFixScheduleData(scheduleData);
  }, []);

  const updateScheduleResetData = useCallback(() => {
    // OPTIMISTIC UPDATE: Reset lock positions in local state
    // This is called when the actual scheduled reset executes
    setProcessedSongs(prev =>
      prev.map(song => ({
        ...song,
        lockPosition: false,
        lockPositionNumber: undefined,
        Edit: (editobj: any) => (
          // Re-render Edit component without lock star
          <div
            style={
              {
                /* existing styles */
              }
            }
          >
            {/* Remove star icon since positions are reset */}
            {/* Keep other Edit component content */}
          </div>
        ),
      }))
    );

    // Update scheduleData to clear lock positions
    setScheduleData(prev =>
      prev.map(item => ({
        ...item,
        lockPosition: false,
        position: undefined,
      }))
    );
  }, []);

  const clearScheduleFixData = useCallback(() => {
    setFixScheduleData(null);
  }, []);

  const getScheduleFixData = useCallback(async (id: string) => {
    try {
      const response = await axios.get(`${BASE_URL}/getfixPlaylistlogs/${id}`);
      if (response?.data?.data?.length) {
        setFixScheduleData(response.data.data[0]);
      } else {
        setFixScheduleData(false);
      }
    } catch (error) {
      console.error('Error fetching fix data:', error);
    }
  }, []);

  // Load additional data when playlist changes
  useEffect(() => {
    if (playlistId) {
      getScheduleRefreshData(playlistId);
      getScheduleFixData(playlistId);
    }
  }, [playlistId, getScheduleRefreshData, getScheduleFixData]);

  // Keyboard shortcuts
  useEffect(() => {
    if (selectedSongs.length > 0) {
      const handleKeyDown = (ev: KeyboardEvent) => {
        if (ev.keyCode === KeyCode('delete')) {
          const isAnyModalOpen = Object.values(modals).some(Boolean);
          if (!isAnyModalOpen) {
            const dontShowBulkDeleteConfirmationAgain = localStorage.getItem('dontShow');
            if (dontShowBulkDeleteConfirmationAgain === 'true') {
              handleBulkDelete();
            } else {
              updateModal('deleteSelected', true);
            }
          }
        } else if (ev.keyCode === KeyCode('esc')) {
          setSelectedSongs([]);
          setSelectedSongsIds([]);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedSongs, modals, handleBulkDelete, updateModal]);

  // Show loading state during initial load, playlist switching, reloading, or when no data is available
  if (isReloading || initialLoad || isLoading || (!currentPlaylistData.id && playlistId)) {
    return (
      <div className="playlistPageContainer" style={{ paddingBottom: '0px' }}>
        <PlaylistNewStyle ref={playlistRef}>
          <div
            style={{
              padding: '60px 20px',
              textAlign: 'center',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <LoadingSpinner
              text={
                isReloading
                  ? 'Saving changes...'
                  : isLoading
                  ? 'Loading playlist...'
                  : 'Preparing playlist...'
              }
              containerPadding="0"
            />
          </div>
        </PlaylistNewStyle>
      </div>
    );
  }

  return (
    <div className="playlistPageContainer" style={{ paddingBottom: '0px' }}>
      <PlaylistNewStyle ref={playlistRef}>
        <PlaylistNewBanner
          title={currentPlaylistData.name || ''}
          totalSongs={`${totalSongs} ${totalSongs > 1 ? 'songs' : 'song'}`}
          desc={currentPlaylistData.description || ''}
          image={propOr('', 'url', head(pathOr('', ['images'], currentPlaylistData)))}
          onScheduleRefreshSuccess={updateScheduleRefreshData}
          onScheduleDeleteSuccess={clearScheduleRefreshData}
          onScheduleResetScheduleSuccess={updateScheduleResetScheduleData}
          onScheduleResetSuccess={updateScheduleResetData}
          onScheduleResetDeleteSuccess={clearScheduleFixData}
          token={loginState.token}
          songs={{ total: totalSongs, items: songs } as any}
          currentPage={1}
          userId={loginState.userId}
          setAddToPlaylistModal={value => updateModal('addToPlaylist', value)}
          handleRefresh={handleRefresh}
          restorePlaylsitSongs={restorePlaylsitSongs}
          setIsModalVisible={value => updateModal('refresh', value)}
          isModalVisible={modals.refresh}
          currentPlaylistData={currentPlaylistData}
          fixed={false}
          PlaylistRefreshRecord={playlistRefreshRecord}
          currentPlaylist={processedSongs}
          refreshPlaylistFunction={() => updateModal('refresh', true)}
          syncdatafromspotify={({ setSyncModal }) => handleResetPositions(setSyncModal)}
          bulkAction={bulkAction}
          setBulkAction={setBulkAction}
          showModalDeleteSelected={() => updateModal('deleteSelected', true)}
          selectedSongs={selectedSongs}
          refreshScheduleData={refreshScheduleData}
          getScheduleRefreshData={getScheduleRefreshData}
          fixScheduleFData={fixScheduleData}
          getScheduleFixData={getScheduleFixData}
          hasFixSong={scheduleData?.filter(obj => obj.lockPosition)?.length > 0}
        />

        <div className="songTableDiv">
          {ENABLE_VIRTUALIZATION ? (
            <VirtualizedSongsTable
              state={processedSongs}
              setState={setProcessedSongs}
              selectedSongs={selectedSongs}
              bulkAction={bulkAction}
              setBulkAction={setBulkAction}
              currentPage={1}
              selectSongFunction={selectSongFunction}
              selectSingleSong={(song, index) => {
                // If this song is already the only selected song, unselect it
                const isAlreadyOnlySelected =
                  selectedSongs.length === 1 && deepEqual(selectedSongs[0], song);
                if (isAlreadyOnlySelected) {
                  setSelectedSongs([]);
                  setSelectedSongsIds([]);
                  setBulkAction(false);
                } else {
                  setSelectedSongs([song]);
                  setSelectedSongsIds(song._id ? [song._id] : []);
                }
              }}
              selectMultpleSongFunction={(arrSelected, ids) => {
                setSelectedSongs(prev => [
                  ...prev,
                  ...arrSelected.filter(item => !prev.some(existing => deepEqual(existing, item))),
                ]);
                setSelectedSongsIds(prev => [
                  ...prev,
                  ...ids.filter(item => !prev.some(existing => deepEqual(existing, item))),
                ]);
              }}
              loadMoreTracks={loadMoreSongs}
              hasMoreTracks={hasMoreData && songs.length < totalSongs}
              onScrollRestore={onScrollRestore}
              handleModalCloseRestoreScroll={handleModalCloseRestoreScroll}
              playlistState={playlistState}
              onSongsReorder={handleSongsReorder}
              isSongplaceOnly={playlistState?.isSongPlace || false}
              clearSelection={() => {
                setSelectedSongs([]);
                setSelectedSongsIds([]);
                setBulkAction(false);
              }}
            />
          ) : (
            <SongsTableNew
              state={processedSongs}
              setState={setProcessedSongs}
              playlistState={playlistState}
              menu={false}
              spotifyTrue={true}
              setIndexes={0}
              numberRef={numberRef}
              titleRef={titleRef}
              popularityRef={popularityRef}
              dateRef={dateRef}
              spotifyRef={spotifyRef}
              actionRef={actionRef}
              bulkAction={bulkAction}
              selectSongFunction={selectSongFunction}
              currentPage={1}
              selectedSongs={selectedSongs}
              setBulkAction={setBulkAction}
              selectMultpleSongFunction={(arrSelected, ids) => {
                setSelectedSongs(prev => [
                  ...prev,
                  ...arrSelected.filter(item => !prev.some(existing => deepEqual(existing, item))),
                ]);
                setSelectedSongsIds(prev => [
                  ...prev,
                  ...ids.filter(item => !prev.some(existing => deepEqual(existing, item))),
                ]);
              }}
              selectSingleSong={(obj, id, index) => {
                // If this song is already the only selected song, unselect it
                const isAlreadyOnlySelected =
                  selectedSongs.length === 1 && deepEqual(selectedSongs[0], obj);
                if (isAlreadyOnlySelected) {
                  setSelectedSongs([]);
                  setSelectedSongsIds([]);
                  setBulkAction(false);
                } else {
                  setSelectedSongs([obj]);
                  setSelectedSongsIds(id ? [id] : []);
                }
              }}
              selectedIndex={0}
              PlaylistRefreshRecordLoader={isLoading}
              loadMoreTracks={loadMoreSongs}
              hasMoreTracks={hasMoreData && songs.length < totalSongs}
              onScrollRestore={onScrollRestore}
              handleModalCloseRestoreScroll={handleModalCloseRestoreScroll}
              onSongsReorder={handleSongsReorder}
            />
          )}
        </div>

        {/* Modals */}
        <AddToPlayListModal
          isModalVisible={modals.addToPlaylist}
          hideArtistAndSongName={true}
          initialPlaylist={
            currentPlaylistData.id
              ? { id: currentPlaylistData.id, name: currentPlaylistData.name || '' }
              : undefined
          }
          setIsModalVisible={value => {
            updateModal('addToPlaylist', value);
            if (!value) {
              // Restore scroll position when modal closes
              handleModalCloseRestoreScroll();
            }
          }}
        />

        <EditAddToPlayListModal
          isModalVisible={modals.editPlaylist}
          setIsModalVisible={value => {
            updateModal('editPlaylist', value);
            if (!value) {
              // Reset editType to prevent stale state affecting future modal opens
              setEditType('');
              // Restore scroll position when modal closes
              handleModalCloseRestoreScroll();
            }
          }}
          editType={editType}
          setEditType={setEditType}
          ViewDetailData={viewDetailData}
          setViewDetailData={setViewDetailData}
          currentPlaylistData={currentPlaylistData}
          PlaylistRefreshRecord={playlistRefreshRecord}
          setIndexes={0}
          loadingText={loadingText}
          loadingTextHead={loadingTextHead}
          playlistIdRef={{ current: playlistId }}
          currentPage={1}
          currentSongs={processedSongs}
          onEndDateSuccess={(songUri, newEndDate, songId) => {
            updateSongEndDate(songUri, newEndDate, songId);
            handleModalCloseRestoreScroll(); // Restore scroll
          }}
          onNotesSuccess={(songUri, newNotes, songId) => {
            updateSongNotes(songUri, newNotes, songId);
            handleModalCloseRestoreScroll(); // Restore scroll
          }}
          onMoveSongSuccess={(songUri, oldPlaylistId, newPlaylistId, newPosition) => {
            updateSongPosition(songUri, oldPlaylistId, newPlaylistId, newPosition);
            handleModalCloseRestoreScroll(); // Restore scroll
          }}
        />

        <ShareModal
          isModalVisible={modals.shareModal}
          setIsModalVisible={value => {
            updateModal('shareModal', value);
            if (!value) {
              // Restore scroll position when modal closes
              handleModalCloseRestoreScroll();
            }
          }}
          editType={editType}
          ViewDetailData={viewDetailData}
          setViewDetailData={setViewDetailData}
          currentPlaylistData={currentPlaylistData}
          PlaylistRefreshRecord={playlistRefreshRecord}
          showCopyUrl={true}
          scheduleSong={false}
        />

        <InstantShareModal
          isModalVisible={modals.instantShare}
          setIsModalVisible={value => {
            updateModal('instantShare', value);
            if (!value) {
              // Restore scroll position when modal closes
              handleModalCloseRestoreScroll();
            }
          }}
          editType={editType}
          currentPlaylistData={currentPlaylistData}
        />

        <DeleteModal
          handleCancel={() => {
            updateModal('deleteModal', false);
            handleModalCloseRestoreScroll();
          }}
          showModal={() => updateModal('deleteModal', true)}
          isModalOpen={modals.deleteModal}
          handleOk={() => {
            updateModal('deleteModal', false);
            handleItemDelete(viewDetailData);
          }}
          heading="Delete from playlist"
          headingText="Are you sure you want to delete this song?"
          positionWarning={
            viewDetailData?.lockPosition ? 'Priority position will also be removed' : ''
          }
          button1="Delete"
          button2="Cancel"
        />

        <DeleteAndFixModal
          handleCancel={() => {
            updateModal('deleteFix', false);
            handleModalCloseRestoreScroll();
          }}
          showModal={() => updateModal('deleteFix', true)}
          isModalOpen={modals.deleteFix}
          handleOk={() => {
            updateModal('deleteFix', false);
            if (fixDataParams) {
              // For position conflicts, just call the callback directly
              // The callback handles the API call and page reload
              fixDataParams.callback();
            }
          }}
          heading="Reset position"
          headingText="This position was previously fixed with a song and not removed manually. To use it for a new song, we must restore it. Would you like to continue?"
          button1="Yes"
          button2="Cancel"
        />

        <DeleteModal
          handleCancel={() => {
            updateModal('deleteSelected', false);
            handleModalCloseRestoreScroll();
          }}
          showModal={() => updateModal('deleteSelected', true)}
          isModalOpen={modals.deleteSelected}
          handleOk={() => {
            updateModal('deleteSelected', false);
            handleBulkDelete();
          }}
          heading={`Delete ${selectedSongs.length} song(s) from playlist`}
          headingText="Are you sure you want to delete these songs?"
          rememberCheckbox={true}
          positionWarning={
            viewDetailData?.lockPosition ? 'Priority position will also be removed' : ''
          }
          button1="Delete"
          button2="Cancel"
        />
      </PlaylistNewStyle>
    </div>
  );
};

export default PlaylistNew;
